import React, { useState, useEffect } from "react";
import { getMockSalaryDetailData } from "./mockData";
import "./SalaryApprovalDetail.css";

const SalaryApprovalDetail = ({ department, onBack }) => {
  const [salaryData, setSalaryData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTimesheetExpanded, setIsTimesheetExpanded] = useState(false);
  const [isMoneyCostsExpanded, setIsMoneyCostsExpanded] = useState(false);
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");

  // Загрузка данных зарплаты
  useEffect(() => {
    if (department) {
      const selectedDepartment = {
        value: department.id,
        label: department.name,
      };
      // Используем дату отдела или текущую дату
      const selectedMonth = department.date || new Date();
      const data = getMockSalaryDetailData(selectedDepartment, selectedMonth);
      setSalaryData(data);
    }
  }, [department]);

  const formatMonthYear = (date) => {
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  // Определение статуса согласования
  const getApprovalStatus = () => {
    if (!department?.approvers || department.approvers.length === 0) {
      return "neutral";
    }

    const statuses = department.approvers.map((a) => a.status);
    const hasRejected = statuses.some((s) => s === "Отклонено");
    const hasPending = statuses.some((s) => s === "На рассмотрении");
    const allApproved = statuses.every((s) => s === "Согласован");

    if (hasRejected) {
      return "rejected";
    } else if (allApproved) {
      return "approved";
    } else if (hasPending) {
      return "pending";
    }
    return "neutral";
  };

  const approvalStatus = getApprovalStatus();

  const getStatusText = () => {
    switch (approvalStatus) {
      case "approved":
        return "Согласован";
      case "rejected":
        return "Отклонено";
      case "pending":
        return "На рассмотрении";
      default:
        return "На рассмотрении";
    }
  };

  const getStatusIcon = () => {
    switch (approvalStatus) {
      case "approved":
        return "✓";
      case "rejected":
        return "✗";
      case "pending":
        return "⏳";
      default:
        return "👥";
    }
  };

  // Определение статуса на основе сравнения рабочих дней и отработанных дней
  const getDaysStatus = (timesheet) => {
    if (!timesheet) return null;
    if (timesheet.daysWorked < timesheet.workingDays) {
      return "error";
    }
    if (timesheet.daysWorked > timesheet.workingDays) {
      return "warning";
    }
    return null;
  };

  // Определение общего статуса строки (для подсветки)
  const getRowStatus = (timesheet) => {
    return getDaysStatus(timesheet);
  };

  const renderStatusIcon = (timesheet, tooltipText) => {
    const status = getDaysStatus(timesheet);
    if (status === "error") {
      return (
        <span
          className="salary-approval-detail__status-icon salary-approval-detail__status-icon--error"
          title={tooltipText || "Отработано дней меньше рабочих дней"}
        >
          ✕
        </span>
      );
    }
    if (status === "warning") {
      return (
        <span
          className="salary-approval-detail__status-icon salary-approval-detail__status-icon--warning"
          title={tooltipText || "Отработано дней больше рабочих дней"}
        >
          ▲
        </span>
      );
    }
    return null;
  };

  const filteredEmployees =
    salaryData?.employees?.filter((employee) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return employee.name.toLowerCase().includes(query);
    }) || [];

  const handleApproveClick = () => {
    setShowConfirmApprove(true);
  };

  const handleConfirmApprove = async () => {
    try {
      console.log("Согласование зарплаты для отдела:", department.name);
      // TODO: Реализовать согласование через API
      setShowConfirmApprove(false);
      // После успешного согласования можно обновить данные или вернуться к списку
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Ошибка при согласовании:", error);
    }
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
    setRejectComment("");
    setRejectCommentError("");
  };

  const handleConfirmReject = async () => {
    // Проверяем, что комментарий заполнен
    if (!rejectComment.trim()) {
      setRejectCommentError("Комментарий обязателен для заполнения");
      return;
    }

    try {
      console.log("Отклонение зарплаты для отдела:", department.name);
      console.log("Комментарий:", rejectComment);
      // TODO: Реализовать отклонение через API с комментарием
      setShowRejectModal(false);
      setRejectComment("");
      setRejectCommentError("");
      // После успешного отклонения можно обновить данные или вернуться к списку
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Ошибка при отклонении:", error);
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectComment("");
    setRejectCommentError("");
  };

  if (!department || !salaryData) {
    return (
      <div className="salary-approval-detail">
        <div className="salary-approval-detail__loading">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="salary-approval-detail">
      {/* Заголовок с кнопкой назад и статусом */}
      <div className="salary-approval-detail__header">
        <button
          className="salary-approval-detail__back-button"
          onClick={onBack}
        >
          <span className="salary-approval-detail__back-arrow">←</span>
          Согласование зарплаты
        </button>
        <div className="salary-approval-detail__title">
          Согласование зарплаты — {department.name}
        </div>
        <div
          className={`salary-approval-detail__status salary-approval-detail__status--${approvalStatus}`}
        >
          <span className="salary-approval-detail__status-icon">
            {getStatusIcon()}
          </span>
          {getStatusText()}
        </div>
      </div>

      {/* Сводная информация */}
      <div className="salary-approval-detail__summary">
        <div className="salary-approval-detail__summary-item">
          <span className="salary-approval-detail__summary-label">Отдел:</span>
          <span className="salary-approval-detail__summary-value">
            {salaryData.departmentName}
          </span>
        </div>
        <div className="salary-approval-detail__summary-item">
          <span className="salary-approval-detail__summary-label">Период:</span>
          <span className="salary-approval-detail__summary-value">
            {formatMonthYear(department.date)}
          </span>
        </div>
        <div className="salary-approval-detail__summary-item">
          <span className="salary-approval-detail__summary-label">
            Количество сотрудников:
          </span>
          <span className="salary-approval-detail__summary-value">
            {salaryData.employeeCount}
          </span>
        </div>
        <div className="salary-approval-detail__summary-item">
          <span className="salary-approval-detail__summary-label">
            Общая сумма к выплате:
          </span>
          <span className="salary-approval-detail__summary-value salary-approval-detail__summary-value--amount">
            {formatCurrency(salaryData.totalAmount)} тг
          </span>
        </div>
        <div className="salary-approval-detail__summary-item">
          <span className="salary-approval-detail__summary-label">
            Выплачено:
          </span>
          <span className="salary-approval-detail__summary-value salary-approval-detail__summary-value--amount">
            {formatCurrency(salaryData.paidAmount)} тг
          </span>
        </div>
        <div className="salary-approval-detail__summary-item">
          <span className="salary-approval-detail__summary-label">
            Остаток:
          </span>
          <span className="salary-approval-detail__summary-value salary-approval-detail__summary-value--amount">
            {formatCurrency(salaryData.remainingAmount)} тг
          </span>
        </div>
        {department.initiator && (
          <div className="salary-approval-detail__summary-item">
            <span className="salary-approval-detail__summary-label">
              Отправил:
            </span>
            <span className="salary-approval-detail__summary-value">
              {department.initiator}
              {department.submissionDate &&
                ` (${formatDate(department.submissionDate)})`}
            </span>
          </div>
        )}
      </div>

      {/* Поиск */}
      <div className="salary-approval-detail__search">
        <span className="salary-approval-detail__search-icon">🔍</span>
        <input
          type="text"
          className="salary-approval-detail__search-input"
          placeholder="Поиск"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Легенда */}
      {isTimesheetExpanded && (
        <div className="salary-approval-detail__legend">
          <div className="salary-approval-detail__legend-title">
            Обозначения:
          </div>
          <div className="salary-approval-detail__legend-items">
            <div className="salary-approval-detail__legend-item">
              <span className="salary-approval-detail__status-icon salary-approval-detail__status-icon--error">
                ✕
              </span>
              <span className="salary-approval-detail__legend-text">
                Отработано дней меньше рабочих дней
              </span>
            </div>
            <div className="salary-approval-detail__legend-item">
              <span className="salary-approval-detail__status-icon salary-approval-detail__status-icon--warning">
                ▲
              </span>
              <span className="salary-approval-detail__legend-text">
                Отработано дней больше рабочих дней
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Таблица */}
      <div className="salary-approval-detail__table-container">
        <table className="salary-approval-detail__table">
          <thead>
            <tr>
              <th rowSpan={2}>ФИО</th>
              <th
                colSpan={isTimesheetExpanded ? 9 : 1}
                className="salary-approval-detail__expandable-header"
                onClick={() => setIsTimesheetExpanded(!isTimesheetExpanded)}
                rowSpan={isTimesheetExpanded ? 1 : 2}
              >
                Табелирование
                <span
                  className={`salary-approval-detail__expand-icon ${
                    isTimesheetExpanded
                      ? "salary-approval-detail__expand-icon--expanded"
                      : ""
                  }`}
                >
                  {isTimesheetExpanded ? "↓" : "→"}
                </span>
              </th>
              <th
                colSpan={isMoneyCostsExpanded ? 5 : 1}
                className="salary-approval-detail__expandable-header"
                onClick={() => setIsMoneyCostsExpanded(!isMoneyCostsExpanded)}
                rowSpan={isMoneyCostsExpanded ? 1 : 2}
              >
                Затраты в деньгах (тг)
                <span
                  className={`salary-approval-detail__expand-icon ${
                    isMoneyCostsExpanded
                      ? "salary-approval-detail__expand-icon--expanded"
                      : ""
                  }`}
                >
                  {isMoneyCostsExpanded ? "↓" : "→"}
                </span>
              </th>
              <th
                colSpan={3}
                className="salary-approval-detail__summary-header"
                rowSpan={1}
              >
                Итоговые показатели
              </th>
            </tr>
            <tr>
              {isTimesheetExpanded && (
                <>
                  <th className="salary-approval-detail__table-header--vertical">
                    Рабочих дней
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Выходных и праздничных дней
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Отработано дней
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Рабочих часов
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Отработано часов
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Командировки
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Ежегодний трудовой отпуск
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Отпуск б/с
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Замещение
                  </th>
                </>
              )}
              {isMoneyCostsExpanded && (
                <>
                  <th className="salary-approval-detail__table-header--vertical">
                    Отработано часов
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Командировки
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Ежегодний трудовой отпуск
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Отпуск б/с
                  </th>
                  <th className="salary-approval-detail__table-header--vertical">
                    Замещение
                  </th>
                </>
              )}
              <th className="salary-approval-detail__summary-subheader">
                Заработная плата
              </th>
              <th className="salary-approval-detail__summary-subheader">
                Выплачено
              </th>
              <th className="salary-approval-detail__summary-subheader">
                Остаток
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((employee) => {
              const rowStatus = getRowStatus(employee.timesheet);
              const rowClassName = `salary-approval-detail__table-row ${
                rowStatus === "warning"
                  ? "salary-approval-detail__table-row--warning"
                  : rowStatus === "error"
                  ? "salary-approval-detail__table-row--error"
                  : ""
              }`;
              return (
                <tr key={employee.id} className={rowClassName}>
                  <td>{employee.name}</td>
                  {isTimesheetExpanded && employee.timesheet ? (
                    <>
                      <td>{employee.timesheet.workingDays}</td>
                      <td>{employee.timesheet.weekendsAndHolidays}</td>
                      <td>
                        {employee.timesheet.daysWorked}
                        {renderStatusIcon(
                          employee.timesheet,
                          `Рабочих дней: ${employee.timesheet.workingDays}, Отработано: ${employee.timesheet.daysWorked}`
                        )}
                      </td>
                      <td>{employee.timesheet.workingHours}</td>
                      <td>{employee.timesheet.hoursWorked}</td>
                      <td>{employee.timesheet.businessTrips}</td>
                      <td>{employee.timesheet.annualLeave}</td>
                      <td>{employee.timesheet.unpaidLeave}</td>
                      <td>
                        {employee.timesheet.substitution ??
                          (employee.timesheet.maternityLeave || 0) +
                            (employee.timesheet.studyLeave || 0) +
                            (employee.timesheet.sickLeave || 0) +
                            (employee.timesheet.absences || 0)}
                      </td>
                    </>
                  ) : (
                    <td className="salary-approval-detail__expandable-cell"></td>
                  )}
                  {isMoneyCostsExpanded && employee.moneyCosts ? (
                    <>
                      <td className="salary-approval-detail__amount-cell">
                        {formatCurrency(employee.moneyCosts.hoursWorked)} тг
                      </td>
                      <td className="salary-approval-detail__amount-cell">
                        {formatCurrency(employee.moneyCosts.businessTrips)} тг
                      </td>
                      <td className="salary-approval-detail__amount-cell">
                        {formatCurrency(employee.moneyCosts.annualLeave)} тг
                      </td>
                      <td className="salary-approval-detail__amount-cell">
                        {formatCurrency(employee.moneyCosts.unpaidLeave)} тг
                      </td>
                      <td className="salary-approval-detail__amount-cell">
                        {formatCurrency(
                          employee.moneyCosts.substitution ??
                            (employee.moneyCosts.maternityLeave || 0) +
                              (employee.moneyCosts.studyLeave || 0) +
                              (employee.moneyCosts.sickLeave || 0) +
                              (employee.moneyCosts.absences || 0)
                        )}{" "}
                        тг
                      </td>
                    </>
                  ) : (
                    <td className="salary-approval-detail__expandable-cell"></td>
                  )}
                  <td className="salary-approval-detail__amount-cell">
                    {formatCurrency(employee.salary)} тг
                  </td>
                  <td className="salary-approval-detail__amount-cell">
                    {formatCurrency(employee.paid)} тг
                  </td>
                  <td className="salary-approval-detail__amount-cell">
                    {formatCurrency(employee.remaining)} тг
                  </td>
                </tr>
              );
            })}
            {/* Итоговая строка */}
            {salaryData && (
              <tr className="salary-approval-detail__table-row salary-approval-detail__table-row--total">
                <td className="salary-approval-detail__total-label">ИТОГО</td>
                {isTimesheetExpanded && (
                  <>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                    <td className="salary-approval-detail__expandable-cell"></td>
                  </>
                )}
                {!isTimesheetExpanded && (
                  <td className="salary-approval-detail__expandable-cell"></td>
                )}
                {isMoneyCostsExpanded && (
                  <>
                    <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                      {formatCurrency(
                        filteredEmployees.reduce(
                          (sum, emp) =>
                            sum + (emp.moneyCosts?.hoursWorked || 0),
                          0
                        )
                      )}{" "}
                      тг
                    </td>
                    <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                      {formatCurrency(
                        filteredEmployees.reduce(
                          (sum, emp) =>
                            sum + (emp.moneyCosts?.businessTrips || 0),
                          0
                        )
                      )}{" "}
                      тг
                    </td>
                    <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                      {formatCurrency(
                        filteredEmployees.reduce(
                          (sum, emp) =>
                            sum + (emp.moneyCosts?.annualLeave || 0),
                          0
                        )
                      )}{" "}
                      тг
                    </td>
                    <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                      {formatCurrency(
                        filteredEmployees.reduce(
                          (sum, emp) =>
                            sum + (emp.moneyCosts?.unpaidLeave || 0),
                          0
                        )
                      )}{" "}
                      тг
                    </td>
                    <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                      {formatCurrency(
                        filteredEmployees.reduce(
                          (sum, emp) =>
                            sum +
                            (emp.moneyCosts?.substitution ??
                              (emp.moneyCosts?.maternityLeave || 0) +
                                (emp.moneyCosts?.studyLeave || 0) +
                                (emp.moneyCosts?.sickLeave || 0) +
                                (emp.moneyCosts?.absences || 0)),
                          0
                        )
                      )}{" "}
                      тг
                    </td>
                  </>
                )}
                {!isMoneyCostsExpanded && (
                  <td className="salary-approval-detail__expandable-cell"></td>
                )}
                <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                  {formatCurrency(salaryData.totalAmount)} тг
                </td>
                <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                  {formatCurrency(salaryData.paidAmount)} тг
                </td>
                <td className="salary-approval-detail__amount-cell salary-approval-detail__amount-cell--total">
                  {formatCurrency(salaryData.remainingAmount)} тг
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Кнопки действий */}
      {approvalStatus === "pending" && (
        <div className="salary-approval-detail__actions">
          <button
            className="salary-approval-detail__button salary-approval-detail__button--reject"
            onClick={handleRejectClick}
          >
            Отклонить
          </button>
          <button
            className="salary-approval-detail__button salary-approval-detail__button--approve"
            onClick={handleApproveClick}
          >
            Согласовать
          </button>
        </div>
      )}

      {/* Попап подтверждения согласования */}
      {showConfirmApprove && (
        <div
          className="popup-overlay"
          onClick={() => setShowConfirmApprove(false)}
        >
          <div
            className="popup popup-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="popup-text">
              Вы уверены, что хотите согласовать зарплату для отдела "
              {department.name}"?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowConfirmApprove(false)}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={handleConfirmApprove}
              >
                Согласовать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Попап отклонения с комментарием */}
      {showRejectModal && (
        <div className="popup-overlay" onClick={handleCancelReject}>
          <div
            className="popup popup-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">Отклонение зарплаты</h3>
            <p className="popup-reject__subtitle">
              Вы уверены, что хотите отклонить зарплату для отдела "
              {department.name}"?
            </p>
            <div className="popup-reject__comment-section">
              <label className="popup-reject__label">
                Комментарий <span className="popup-reject__required">*</span>
              </label>
              <textarea
                className={`popup-reject__textarea ${
                  rejectCommentError ? "popup-reject__textarea--error" : ""
                }`}
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  if (rejectCommentError) {
                    setRejectCommentError("");
                  }
                }}
                placeholder="Укажите причину отклонения..."
                rows={4}
              />
              {rejectCommentError && (
                <span className="popup-reject__error">
                  {rejectCommentError}
                </span>
              )}
            </div>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={handleCancelReject}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={handleConfirmReject}
              >
                Отклонить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryApprovalDetail;
